import { CheckOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import { LocalMessage } from 'hooks/business/conversation';
import { useSingleOrBatchTrans } from 'hooks/business/im/useSingleOrBatchTrans';
import { noop, uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import useMessageRender from 'views/Conversation/Chat/components/ChatMain/useMessageRender';
import { ChatProvider } from 'views/Conversation/Chat/contexts/ChatContext';
import { UIMessages, useUiMessage } from 'views/Conversation/Chat/useChatView';

interface MessagesProps {
  messages: LocalMessage[];
  conversation: Conversation.Conversation;
  selectedMessages: Conversation.Message[];
  onSelectedMessagesChange: (messages: Conversation.Message[]) => void;
}

interface MessageItemProps {
  checked?: boolean;
  message: UIMessages;
  conversation: Conversation.Conversation;
  onCheck?: (message: Conversation.Message, checked: boolean) => void;
}
const MessageItem = (props: MessageItemProps) => {
  const { checked, message, conversation, onCheck } = props;
  const { messageRender } = useMessageRender(conversation, []);

  const msgContent = messageRender(message);

  return (
    <div
      className={cx('relative p-2 flex items-center justify-between', {
        'bg-[#DFE0E3]': checked,
      })}
      onClick={(e) => {
        e.stopPropagation();
        onCheck?.(message.raw, !checked);
      }}
    >
      <div
        className={cx(
          'w-[20px] h-[20px] rounded-full border border-solid border-[#A7A7A7] flex-shrink-0 cursor-pointer hover:border-[#404040] flex items-center justify-center text-[#FFFFFF]',
          {
            'bg-[#404040] border-[#404040]': checked,
          },
        )}
      >
        {checked && <CheckOutlined />}
      </div>
      {msgContent}
    </div>
  );
};

export const MessagesPanel = (props: MessagesProps) => {
  const { messages, conversation, selectedMessages, onSelectedMessagesChange } = props;
  const singleOrBatchTrans = useSingleOrBatchTrans();

  const { uiMessages } = useUiMessage({
    messages: messages || [],
    conversation,
  });

  const filteredMessages = useMemo(() => {
    return uiMessages.filter((item) => !!item);
  }, [uiMessages]);

  const contextValue = useMemo(() => {
    return {
      isGalaxy: true,
      dialogRef: null,
      setDialogRef: noop,
      clickQuote: noop,
      singleTransToSysLang: singleOrBatchTrans,
      showFeedback: noop,
      setContextMenuMessage: noop,
      setContextMenuSingleType: noop,
      showContextMenu: noop,
      videoTextState: undefined,
      setVideoTextState: noop,
      highlightMsg: undefined,
      hasQAPairMounted: false,
      setHasQAPairMounted: noop,
      hideFooter: true,
      showSourceContent: false,
    };
  }, [singleOrBatchTrans]);

  return (
    <ChatProvider value={contextValue}>
      <div className="flex flex-col gap-2 bg-[#EEF0F4] h-[300px] overflow-y-auto">
        {filteredMessages.map((item) => {
          return (
            <MessageItem
              key={item.raw.msgId}
              message={item}
              conversation={conversation}
              checked={selectedMessages.some((message) => message.msgId === item.raw.msgId)}
              onCheck={(message, checked) => {
                if (checked) {
                  onSelectedMessagesChange(uniqBy([...selectedMessages, message], 'msgId'));
                } else {
                  onSelectedMessagesChange(
                    selectedMessages.filter((m) => m.msgId !== message.msgId),
                  );
                }
              }}
            />
          );
        })}
      </div>
    </ChatProvider>
  );
};
